import { httpBatchLink, httpLink, loggerLink, splitLink } from '@trpc/client'
import { createTRPCNext } from '@trpc/next'
import superjson from 'superjson'
import { observable } from '@trpc/server/observable'
import { MutationCache, QueryCache } from '@tanstack/react-query'
import { logger } from '@loc/helpers/src/logger/client'

import { onQueryClientError, throwGlobalTrpcError } from './error'
import { apiEventDispatchLink } from './apiEventDispatchLink'

import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import type { TRPCLink } from '@trpc/client'
import type { WebRouter } from '@loc/server'

const getTRPCApiUrl = () => {
  // browser should use relative url
  if (typeof window !== 'undefined') {
    return '/api/trpc'
  }
  // SSR should use vercel url
  // if (process.env.VERCEL_URL) {
  //   return `https://${process.env.VERCEL_URL}`;
  // }
  return `http://localhost:${process.env.PORT ?? 3000}/api/trpc` // dev SSR should use localhost
}

let commonHeaders: Record<string, string> = {}

export const setCommonHeaders = (
  setHeaders: (headers: Record<string, string>) => Record<string, string>,
) => {
  commonHeaders = setHeaders(commonHeaders)
}

export const getCommonHeaders = () => {
  return {
    ...commonHeaders,
  }
}

// custom link: https://trpc.io/docs/client/links#creating-a-custom-link
const throwMessageLink: TRPCLink<WebRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          observer.next(value)
        },
        error(error) {
          // 通过 queryClientConfig 的 onError 配置不会触发 abort 回调，
          // 自定义 link 先临时解决问题。
          //
          // 当前已知的 abort 错误消息：
          // 'Aborted'
          // 'signal is aborted without reason'
          // 'The user aborted a request.'
          // "Failed to execute 'fetch' on 'Window': The user aborted a request."
          if (
            error.name === 'TRPCClientError' &&
            /abort/i.test(error.message)
          ) {
            observer.error(error)
            return
          }
          const formattedErrorInfo = [
            'name:',
            error.name,
            '\n',
            'meta:',
            error.meta,
            '\n',
            'data:',
            error.data,
            '\n',
            'shape:',
            error.shape,
            '\n',
            'message:',
            error.message,
            '\n',
            'cause:',
            error.cause,
            '\n',
            'stack:',
            error.stack,
          ]
          logger.debug('AppTRPCClientError:\n', ...formattedErrorInfo)

          /**
           * 关闭所有错误提示：
           *  trpc: {
           *    context: {
           *      ignoreError: true
           *    }
           *  }
           */
          const ignoreError =
            typeof op.context.skipError === 'boolean' &&
            op.context.skipError === true
          throwGlobalTrpcError(error, ignoreError)
          observer.error(error)
        },
        complete() {
          observer.complete()
        },
      })
      return unsubscribe
    })
  }
}

/** A set of type-safe react-query hooks for your tRPC API. */
export const api = createTRPCNext<WebRouter>({
  config() {
    const url = getTRPCApiUrl()
    return {
      /**
       * Transformer used for data de-serialization from the server.
       *
       * @see https://trpc.io/docs/data-transformers
       */
      transformer: superjson,

      /**
       * Links used to determine request flow from client to server.
       *
       * @see https://trpc.io/docs/links
       */
      links: [
        loggerLink(),
        throwMessageLink,
        apiEventDispatchLink,
        splitLink({
          condition: (op) => {
            return op.context.skipBatch === true
          },
          true: httpLink({
            url,
            headers: getCommonHeaders,
          }),
          false: httpBatchLink({ url, headers: getCommonHeaders }),
        }),
      ],

      abortOnUnmount: true,
      // 全局异常捕获，只处理额度不足弹窗这样的全局不可覆盖的异常
      queryClientConfig: {
        queryCache: new QueryCache({
          onError: onQueryClientError,
        }),
        mutationCache: new MutationCache({
          onError: onQueryClientError,
        }),

        defaultOptions: {
          queries: {
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            retry: 2,
          },
        },
      },
    }
  },
  /**
   * Whether tRPC should await queries when server rendering pages.
   *
   * @see https://trpc.io/docs/nextjs#ssr-boolean-default-false
   */
  ssr: false,
})

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<WebRouter>

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<WebRouter>
